import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { RegistrarDriftDto } from "@/types/domain-drift";

interface DomainDriftResolveDialogProps {
  /** Drift row being resolved; null closes the dialog. */
  drift: RegistrarDriftDto | null;
  isSubmitting: boolean;
  onClose: () => void;
  /**
   * Called with the trimmed admin notes, or `undefined` when the textarea
   * is empty. The backend stores the notes verbatim for the audit trail.
   */
  onSubmit: (notes: string | undefined) => void;
}

/**
 * Optional-notes confirm dialog for resolving a registrar-drift row.
 * Modelled after `DomainActionConfirmDialog`, but notes are optional —
 * the resolve endpoint accepts an empty body.
 */
export function DomainDriftResolveDialog({
  drift,
  isSubmitting,
  onClose,
  onSubmit,
}: DomainDriftResolveDialogProps) {
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (drift) setNotes("");
  }, [drift]);

  const open = drift !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Resolve drift on {drift?.fqdn ?? drift?.domainRegistrationId ?? ""}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            Mark this drift row as resolved. You may add optional notes to
            describe the corrective action; the notes are recorded for the audit
            trail.
          </DialogDescription>
          {drift && (
            <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-xs">
              <div>
                <span className="font-medium">Drift:</span> {drift.driftType}
              </div>
              <div>
                <span className="font-medium">Local:</span>{" "}
                <code>{drift.localValue ?? "—"}</code>
              </div>
              <div>
                <span className="font-medium">Registrar:</span>{" "}
                <code>{drift.registrarValue ?? "—"}</code>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="domain-drift-notes">Notes (optional)</Label>
            <Textarea
              id="domain-drift-notes"
              placeholder="What was done to reconcile this drift?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              const trimmed = notes.trim();
              onSubmit(trimmed.length > 0 ? trimmed : undefined);
            }}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
